import { useCallback, useEffect, useState } from 'react';

import { useMediaQuery } from '@react-hookz/web';
import LogRocket from 'logrocket';
import qs from 'query-string';
import type { RouteComponentProps } from 'react-router';
import { useTheme } from 'styled-components';

import { useAccount } from 'src/app/hooks/data/account';
import type {
  HealthProfileMedicationToVerifyResult,
  HealthProfileMedicationToVerifyWithSearch,
} from 'src/app/hooks/data/healthProfile';
import {
  useHealthProfileMedicationsToVerify,
  useIsHealthProfileUpdateDue,
  useUpdateHealthProfileMedications,
} from 'src/app/hooks/data/healthProfile';
import { usePrimaryPatient, usePatientUpdate } from 'src/app/hooks/data/patient';
import { useCreateHealthProfileTransferTask, useNotifyPharmacy } from 'src/app/hooks/data/prescriptionTransfers';
import { useUser } from 'src/app/hooks/data/user';
import type { HealthProfileDataV2 } from 'src/app/hooks/feature_data/useHealthProfilePersistentData';
import { useHealthProfileV2PersistentData } from 'src/app/hooks/feature_data/useHealthProfilePersistentData';
import { useHealthProfileSessionData } from 'src/app/hooks/feature_data/useHealthProfileSessionData';
import useAuth from 'src/app/hooks/useAuth';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { medications as medicationsHome } from 'src/app/router/paths';
import { toggleHideHeader } from 'src/app/state/actions';
import { useDispatch } from 'src/app/state/store';
import { useLogRocketInit } from 'src/hooks/useLogRocketInit';

import Spinner from '../../Elements/Spinner';
import { SimpleFeatureLayoutPageContainer } from '../../FeatureLayouts/SimpleFeatureLayout/SimpleFeatureLayout';

import { Confirmation } from './Confirmation';
import { HealthProfileUpdateStepNames } from './constants';
import { GotIt } from './GotIt';
import { JustSayCapsuleConfirmation } from './JustSayCapsuleConfirmation';
import { MedicationPicker } from './MedicationPicker';
import { MoveYourMeds } from './MoveYourMeds';
import { SearchMedications } from './SearchMedications';
import { HealthProfileFormHeaderAdjustment } from './styles';
import {
  formatMedicationWithStrengthAndLabel,
  getMedicationsTransferEligibility,
  splitInternalExternalSearchedMedications,
} from './utils';

export interface HealthProfileFormProps {
  onToggleHideHeader: (toggle: boolean) => void;
  isMobile: boolean;
  healthProfilePersistentData: HealthProfileDataV2;
  setHealthProfilePersistentData: (newContext: HealthProfileDataV2) => Promise<void>;
  isLoadingPersistentData: boolean;
  updateMedications: () => Promise<void>;
}

interface HealthProfileFormParams {
  stepName: string;
  source?: string;
}

export const HealthProfileUpdateComponent = (props: RouteComponentProps<HealthProfileFormParams>) => {
  const { history, match, location } = props;
  useLogRocketInit();
  const currentStepName = match.params.stepName as HealthProfileUpdateStepNames;
  const { source } = qs.parse(location.search) as { source?: string };

  // Layout setup
  const theme = useTheme();
  const isMobile = !!useMediaQuery(theme.breakpoints.mediumDown);
  const [navHeightOverride, setNavHeightOverride] = useState(theme.space.loggedInHeaderNavHeight);
  // Only way to disable the nav header is using redux, so we have to use some redux here
  const dispatch = useDispatch();
  const onToggleHideHeader = (toggle: boolean) => {
    dispatch(toggleHideHeader(toggle));
    setNavHeightOverride(toggle ? theme.space.s6 : theme.space.loggedInHeaderNavHeight);
  };

  // User and experiment setup
  const { loggedInUser, isLoggedIn } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', isLoggedIn);
  const accountId = user?.accountId ?? '';
  const { data: account } = useAccount(user ? parseInt(user.accountId) : -1, {
    enabled: true,
    refetchOnWindowFocus: false,
  });
  const zipCode = account?.zipCode;
  const { data: primaryPatient } = usePrimaryPatient();
  const patientId = primaryPatient !== null ? primaryPatient.id : '';

  useEffect(() => {
    if (user) {
      try {
        LogRocket.identify(user.accountId);
      } catch (err) {
        console.error('Failed to identify user for LogRocket.', err);
      }
    }
  }, [user]);

  const { healthProfilePersistentData, setHealthProfilePersistentData, isLoadingPersistentData } =
    useHealthProfileV2PersistentData();

  const { setHealthProfileSessionData } = useHealthProfileSessionData();
  const { data: isHealthProfileUpdateDue, isLoading: isLoadingHealthProfileUpdateDue } =
    useIsHealthProfileUpdateDue(patientId);

  /*
   * Set session storage flag that the user has seen the form so
   * we don't show it again if they bail out.
   */
  useEffect(() => {
    setHealthProfileSessionData({ hasSeenForm: true });
  }, []);

  // If user is ineligible to see the Health Profile Update form, redirect them out to RX Home
  useEffect(() => {
    if (!isLoadingHealthProfileUpdateDue) {
      const checkIsHealthProfileUpdateDue = async () => {
        if (!isHealthProfileUpdateDue) {
          history.push(medicationsHome.toPath());
        }
      };
      checkIsHealthProfileUpdateDue();
    }
  }, [isLoadingHealthProfileUpdateDue, isHealthProfileUpdateDue, history]);

  // Data hooks: RXLive medications and prescription transfer task
  const { data: medications, isLoading } = useHealthProfileMedicationsToVerify(patientId);
  const { mutateAsync: createHealthProfileTransfer } = useCreateHealthProfileTransferTask();
  const { mutateAsync: notifyPharmacy } = useNotifyPharmacy(accountId);
  const { mutateAsync: updatePatient } = usePatientUpdate(parseInt(patientId), parseInt(accountId));
  const { mutateAsync: updateHealthProfileMedications } = useUpdateHealthProfileMedications();

  // Analytics
  const dispatchSaveHealthProfile = useSegmentEvent('saveHealthProfile', {
    shouldTrackOnMount: false,
  });
  const dispatchSaveHealthProfileNoMeds = useSegmentEvent('saveHealthProfileNoMeds', {
    shouldTrackOnMount: false,
  });

  // Create health profile prescription transfer task and notify pharmacy
  const createTransferTask = useCallback(async () => {
    const selectedMeds = healthProfilePersistentData.selectedMeds.filter(
      (med: HealthProfileMedicationToVerifyWithSearch) => !med.isCapsuleMed
    );
    const { eligibleMeds } = getMedicationsTransferEligibility(selectedMeds);
    const { externalMedications, searchedMedications } = splitInternalExternalSearchedMedications(eligibleMeds);

    await createHealthProfileTransfer({
      patientId,
      searchedMedications: searchedMedications.map(med => ({
        name: formatMedicationWithStrengthAndLabel(med),
        hiclSeqno: med.hiclSeqno,
      })),
      externalMedicationIds: externalMedications.map(med => med.externalId),
    });
    await notifyPharmacy({});
  }, [createHealthProfileTransfer, healthProfilePersistentData.selectedMeds, notifyPharmacy, patientId]);

  // Update Health Profile Medications and Pharmakon otherMedications. Fire Save Health Profile event.
  const updateMedications = useCallback(async () => {
    const selectedMeds = healthProfilePersistentData.selectedMeds;

    const medicationNames = selectedMeds.map(med => formatMedicationWithStrengthAndLabel(med));

    try {
      const updatePharmakon = updatePatient({ otherMedications: medicationNames.join(', ') });
      /*
       * We can't save medications with no id (e.g. a Capsule medication) or externalId
       * (e.g.: a RX Live medication) to patient-service so we need to filter them out here.
       * This means no search meds, basically.
       */
      const { externalMedications, capsuleMedications, searchedMedications } =
        splitInternalExternalSearchedMedications(selectedMeds);
      const formSelectedMeds = [...externalMedications, ...capsuleMedications];
      const healthProfileEligibleMeds = formSelectedMeds.map((med: HealthProfileMedicationToVerifyResult) => {
        const baseObject = med.isCapsuleMed
          ? { patientMedicationId: med.id }
          : { externalPatientMedicationId: med.externalId };

        return {
          ...baseObject,
          ...(med.gcn !== null && { gcn: med.gcn }),
        };
      });

      const updatePatientService = updateHealthProfileMedications({
        patientId,
        medications: healthProfileEligibleMeds,
      });
      // Medication data
      const allMedicationsShown = medications?.map(med => med.medications).flat();
      const allMedicationsShownCount = allMedicationsShown?.length || 0;
      const capsuleMedsShown = allMedicationsShown?.filter(med => !!med?.isCapsuleMed);
      const capsuleMedsShownCount = capsuleMedsShown?.length || 0;
      const nonCapsuleMedsShown = allMedicationsShown?.filter(med => !med?.isCapsuleMed);
      const formSelectedMedsNames = formSelectedMeds.map(med => formatMedicationWithStrengthAndLabel(med));
      const searchMedsNames = searchedMedications.map(med => formatMedicationWithStrengthAndLabel(med));

      // Fire event based on whether the user selected any medications
      if (formSelectedMeds.length > 0) {
        dispatchSaveHealthProfile({
          medications: formSelectedMedsNames,
          capsule_medications: capsuleMedsShown?.map(med => med.primaryName) || [''],
          noncapsule_medications: nonCapsuleMedsShown?.map(med => med.primaryName) || [''],
          searched_med: searchMedsNames,
          num_total_medications_shown: allMedicationsShownCount,
          perc_total_medications_selected: Math.round((formSelectedMeds.length / allMedicationsShownCount) * 100),
          num_capsule_medications_shown: capsuleMedsShownCount,
          perc_capsule_medications_selected: Math.round((capsuleMedications.length / capsuleMedsShownCount) * 100),
          num_noncapsule_medications_shown: allMedicationsShownCount - capsuleMedsShownCount,
          perc_noncapsule_medications_selected: Math.round(
            (externalMedications.length / (allMedicationsShownCount - capsuleMedsShownCount)) * 100
          ),
        });
      } else {
        dispatchSaveHealthProfileNoMeds({
          medications: allMedicationsShown?.map(med => med.primaryName) || [''],
          num_total_medications_shown: allMedicationsShownCount,
          capsule_medications: capsuleMedsShown?.map(med => med.primaryName) || [''],
          num_capsule_medications_shown: capsuleMedsShownCount,
          num_noncapsule_medications_shown: allMedicationsShownCount - capsuleMedsShownCount,
          noncapsule_medications: nonCapsuleMedsShown?.map(med => med.primaryName) || [''],
        });
      }
      await Promise.all([updatePharmakon, updatePatientService]);
    } catch (error) {
      console.error('Failed to update patient medications remotely', error);
    }
  }, [
    dispatchSaveHealthProfile,
    dispatchSaveHealthProfileNoMeds,
    healthProfilePersistentData.selectedMeds,
    medications,
    patientId,
    updateHealthProfileMedications,
    updatePatient,
  ]);

  const renderedStepComponent = (props: HealthProfileFormProps) => {
    switch (currentStepName) {
      case HealthProfileUpdateStepNames.SelectTakingMedications:
        return (
          <MedicationPicker
            zipCode={zipCode}
            isLoading={isLoading}
            healthProfileMedications={medications}
            source={source}
            {...props}
          />
        );
      case HealthProfileUpdateStepNames.ConfirmMoveMedications:
        return <MoveYourMeds createTransferTask={createTransferTask} {...props} />;
      case HealthProfileUpdateStepNames.Confirmation:
        return <Confirmation {...props} />;
      case HealthProfileUpdateStepNames.GotIt:
        return <GotIt {...props} />;
      case HealthProfileUpdateStepNames.JustSayCapsule:
        return <JustSayCapsuleConfirmation {...props} />;
      case HealthProfileUpdateStepNames.SearchMedications:
        return <SearchMedications {...props} zipCode={zipCode} healthProfileMedications={medications} />;
      default:
        return <Spinner isFullViewportSpinner />;
    }
  };

  /* Undo header-hiding shenanigans when leaving */
  useEffect(() => {
    window.addEventListener('beforeunload', () => {
      onToggleHideHeader(false);
    });
    return () => {
      onToggleHideHeader(false);
      window.removeEventListener('beforeunload', () => {
        onToggleHideHeader(false);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <HealthProfileFormHeaderAdjustment currentStepName={currentStepName}>
      <SimpleFeatureLayoutPageContainer
        key={currentStepName}
        children={renderedStepComponent({
          onToggleHideHeader,
          isMobile,
          healthProfilePersistentData,
          setHealthProfilePersistentData,
          isLoadingPersistentData,
          updateMedications,
        })}
        navHeightOverride={navHeightOverride}
      />
    </HealthProfileFormHeaderAdjustment>
  );
};
